import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme, Select, MenuItem, Divider } from '@mui/material';
import SideBar from '../../../../components/shared-menu/Sidebar';
import OverlayLoader from '../../../../components/overlayLoader';
import ActiveCases from './tabs/ActiveCases';
import AppliedLoans from './tabs/AppliedLoans';
import RejectedApplications from './tabs/RejectedApplications';
import CancelledApplications from './tabs/CancelledApplications';
import UnserviceableApplications from './tabs/UnserviceableApplications';
import { loanTypeMap } from './utils';
import loanTypesService from '../../../../services/api/loanTypes';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import SentToField from './tabs/SentToField';
import { useAuth } from '../../../../services/auth/AuthContext';
import DownloadIcon from '@mui/icons-material/Download';
import ExportModal from './components/ExportModal';

export default function DashboardForLoanType({ loanType, userProfile, setLoanType }) {
	const theme = useTheme();
	const [activeLoanTypeCodes, setActiveLoanTypeCodes] = useState(null);

	// Which loan types exist is the backend's answer; which of them this
	// dashboard can draw is the frontend's. A product is offered only when both
	// agree - an active product with no status content would render a tab strip
	// with nothing in it and throw on the first status lookup.
	//
	// Until the request resolves, and if it fails, fall back to everything the
	// dashboard knows how to render. A failed lookup should not empty the
	// selector and strand the user on a blank page.
	const selectableLoanTypes = useMemo(() => {
		const renderable = Object.entries(loanTypeMap);
		if (!activeLoanTypeCodes) return renderable;
		return renderable.filter(
			([code]) => code === 'null' || activeLoanTypeCodes.includes(code)
		);
	}, [activeLoanTypeCodes]);

	useEffect(() => {
		let cancelled = false;
		loanTypesService
			.getActiveLoanTypes()
			.then((response) => {
				const codes = (response?.data?.loan_types ?? response?.loan_types ?? [])
					.map(({ code }) => code)
					.filter(Boolean);
				if (!cancelled && codes.length) setActiveLoanTypeCodes(codes);
			})
			.catch(() => {
				// Leave the fallback in place; the selector stays usable.
			});
		return () => {
			cancelled = true;
		};
	}, []);
	const isLgDownScreen = useMediaQuery(theme.breakpoints.down("lg"));
	const isSmDownScreen = useMediaQuery(theme.breakpoints.down("sm"));

	const { t } = useTranslation();

	const { user: authUser } = useAuth();
	const userRoles = authUser.role;

	const loan_type_pages = [
		{
			label: 'dashboard.pages.in_progress',
			value: 'in_progress',
			allowedRoles: ['*']
		},
		{
			label: 'dashboard.pages.submitted',
			value: 'submitted',
			allowedRoles: ['*'],
		},
		{
			label: 'dashboard.pages.cancelled',
			value: '_cancelled',
			children: ['rejected', 'unserviceable', 'cancelled'],
			allowedRoles: ['*'],
		},
		{
			label: 'dashboard.pages.rejected',
			value: 'rejected',
			parent: '_cancelled',
			allowedRoles: ['*'],
		},
		{
			label: 'dashboard.pages.cancelled',
			value: 'cancelled',
			parent: '_cancelled',
			allowedRoles: ['*'],
		},
		{
			label: 'dashboard.pages.unserviceable',
			value: 'unserviceable',
			parent: '_cancelled',
			allowedRoles: ['*'],
		},
		{
			label: 'dashboard.pages.sent_to_field',
			value: 'sent_to_field',
			allowedRoles: ['*'],
		},
		{
			label: 'dashboard.pages.disbursed',
			value: 'disbursed',
			allowedRoles: ['*']
		},
	];

	const chat_initiated_pages = [
		{
			label: 'dashboard.pages.in_progress',
			value: 'in_progress',
			allowedRoles: ['*']
		},
		{
			label: 'dashboard.pages.rejected',
			value: 'rejected',
			allowedRoles: ['*'],
		},
	];

	const pages = loanType === 'null' ? chat_initiated_pages : loan_type_pages;

	const [loading, setLoading] = useState(false);

	const [searchParams, setSearchParams] = useSearchParams({ page: pages[0].value });
	const currentPage = searchParams.get('page') ?? pages[0].value;
	const setCurrentPage = (page) => setSearchParams((searchParams) => {
		searchParams.set('page', page.value);
		searchParams.delete('status');
		return searchParams;
	});
    const setCurrentStatusCode = (status) => setSearchParams((searchParams) => {
        searchParams.set('status', status);
        return searchParams;
    });

	const [exportOpen, setExportOpen] = useState(false);

	// The modal opens showing the section and filters already in play, so the
	// common case - "the list I am looking at, as a file" - is one click.
	const PAGE_TO_APPLICATION_STATUS = {
		in_progress: 'pending',
		submitted: 'submitted',
		rejected: 'rejected',
		cancelled: 'cancelled',
		unserviceable: 'unserviceable',
		sent_to_field: 'pushed_to_field',
	};

	const exportPrefill = useMemo(() => ({
		loan_type_code: loanType === 'null' ? 'all' : loanType,
		current_application_status: PAGE_TO_APPLICATION_STATUS[currentPage] ?? 'all',
		state: searchParams.get('state') ?? 'all',
		// PAGE_TO_APPLICATION_STATUS is a literal map, not state: listing it as a
		// dependency would rebuild the prefill on every render, which is exactly
		// what this useMemo exists to prevent.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}), [loanType, currentPage, searchParams]);

	// Empty and blocked states render in place of a card list, so they need the
	// same breathing room the list has rather than sitting in the corner.
	const PageMessage = ({ children }) => (
		<Box
			display="flex"
			justifyContent="center"
			textAlign="center"
			px={3}
			py={8}
		>
			<Typography color="text.secondary">{children}</Typography>
		</Box>
	);

	const renderPage = (page, loanType) => {
		const allowedRoles = pages.find(({ value }) => page === value)?.allowedRoles ?? [];

		if (
			!allowedRoles.includes('*') &&
			!allowedRoles.some(role => userRoles.includes(role))
		) {
			return (
				<PageMessage>
					You are not allowed to view this page
				</PageMessage>
			);
		}

		if (loanType.toLowerCase() === 'null') {
			switch (page) {
				case 'in_progress':
					return (
						<ActiveCases
							key={loanType}
							loanType={loanType}
							setCurrentStatusCode={setCurrentStatusCode}
						/>
					);
				case 'rejected':
					return (
						<RejectedApplications
							key={loanType}
							loanType={loanType}
							setCurrentStatusCode={setCurrentStatusCode}
						/>
					);

				default:
					return <PageMessage>No Enquiries available</PageMessage>;
			}
		} else {
			switch (page) {
				case 'in_progress':
					return (
						<ActiveCases
							key={loanType}
							loanType={loanType}
							setCurrentStatusCode={setCurrentStatusCode}
						/>
					);
				case 'submitted':
					return (
						<AppliedLoans
							key={loanType}
							loanType={loanType}
							setCurrentStatusCode={setCurrentStatusCode}
						/>
					);
				case 'rejected':
					return (
						<RejectedApplications
							key={loanType}
							loanType={loanType}
							setCurrentStatusCode={setCurrentStatusCode}
						/>
					);

				case 'cancelled':
					return (
						<CancelledApplications
							key={loanType}
							loanType={loanType}
							setCurrentStatusCode={setCurrentStatusCode}
						/>
					);
				case 'unserviceable':
					return (
						<UnserviceableApplications
							key={loanType}
							loanType={loanType}
							setCurrentStatusCode={setCurrentStatusCode}
						/>
					);
				case 'sent_to_field':
					return (
						<SentToField
							key={loanType}
							loanType={loanType}
							setCurrentStatusCode={setCurrentStatusCode}
						/>
					);

				default:
					return <PageMessage>No Enquiries available</PageMessage>;
			}
		}
	};

	return (
		<Box
			height={'100%'}
			overflow={'auto'}
			display={'flex'}
			flexDirection={isLgDownScreen ? 'column' : 'row'}
		>
			<Box
				{...((isLgDownScreen || isSmDownScreen) && {
					display: 'flex',
					flexDirection: isSmDownScreen ? 'column' : 'row',
					alignItems: 'center',
					gap: 1,
					p: 1,
					sx: {
						backgroundColor: 'hsl(0, 0%, 14.9%)',
					},
				})}
			>
				<Select
					value={loanType}
					onChange={(e) => setLoanType(e.target.value)}
					sx={{
						m: !isLgDownScreen ? 1 : 0,
						flexBasis: '50%',
						width: isLgDownScreen ? '100%' : '180px',
					}}
				>
					{selectableLoanTypes.map(([loanTypeCode, { label }]) => (
						<MenuItem
							key={loanTypeCode}
							value={loanTypeCode}
						>
							{t(label)}
						</MenuItem>
					))}
				</Select>

				{!isLgDownScreen && (
					<Divider sx={{ mx: 1 }} />
				)}

				<Box flexBasis={'50%'} width={'100%'}>
					<SideBar
						pages={pages}
						selectedPage={currentPage}
						setSelection={setCurrentPage}
						footer={(
							<Button
								fullWidth
								size="small"
								variant="outlined"
								startIcon={<DownloadIcon />}
								onClick={() => setExportOpen(true)}
							>
								Export CSV
							</Button>
						)}
					/>
				</Box>
			</Box>

			<OverlayLoader
				open={loading}
				text={t('loading.loan_applications') + '...'}
			/>

			<ExportModal
				open={exportOpen}
				onClose={() => setExportOpen(false)}
				initialFilters={exportPrefill}
			/>

			<Box
				height={'100%'}
				overflow={'auto'}
				width="100%"
				sx={(theme) => !isLgDownScreen && ({ borderLeft: `1px solid ${theme.palette.divider}` })}
			>
				{renderPage(currentPage, loanType)}
			</Box>
		</Box>
	);
}
